import type { ExternalLayerAccessType } from 'hooks/externalLayer/types';
import { ordinalCodesMatching } from './fieldSemantics';

/** Projection of `__esri.Field.type` values we expose in the rule builder. */
export type ArcGisFieldType =
  | 'small-integer'
  | 'integer'
  | 'big-integer'
  | 'single'
  | 'double'
  | 'long'
  | 'string'
  | 'date'
  | 'oid';

export type ArcGisCodedValue = {
  name: string;
  code: string | number;
};

export type ArcGisFieldSchema = {
  name: string;
  alias: string;
  type: ArcGisFieldType;
  codedValues?: ArcGisCodedValue[];
  range?: { min: number; max: number };
};

export type ArcGisLayerSchema = {
  layerLabel: string;
  objectIdField: string;
  displayField: string | null;
  fields: ArcGisFieldSchema[];
  capabilities: {
    supportsQuery: boolean;
    supportsDistinct: boolean;
  };
};

/** Operators the current coms evaluator can honor for custom layers. */
export type ArcGisConditionOperator = '>' | '>=' | '<' | '<=' | '==';

export type ArcGisFieldSnapshot = {
  name: string;
  alias: string;
  type: ArcGisFieldType;
  codedValues?: ArcGisCodedValue[];
};

export type ArcGisAuthErrorCode = 403 | 498 | 499 | 'unknown';

export type ArcGisSchemaAuthError = {
  code: ArcGisAuthErrorCode;
  message: string;
};

export const NUMERIC_FIELD_TYPES: ReadonlySet<ArcGisFieldType> = new Set([
  'small-integer',
  'integer',
  'big-integer',
  'single',
  'double',
  'long',
  'oid',
]);

const NON_SELECTABLE_ESRI_TYPES = new Set([
  'geometry',
  'blob',
  'raster',
  'xml',
  'global-id',
  'guid',
  'date-only',
  'time-only',
  'timestamp-offset',
]);

export function isNumericFieldType(type: ArcGisFieldType): boolean {
  return NUMERIC_FIELD_TYPES.has(type);
}

export function isSelectableFieldType(type: string): boolean {
  return !NON_SELECTABLE_ESRI_TYPES.has(type);
}

export function mapEsriFieldType(type: string): ArcGisFieldType | null {
  if (!isSelectableFieldType(type)) return null;
  switch (type) {
    case 'small-integer':
    case 'integer':
    case 'big-integer':
    case 'single':
    case 'double':
    case 'long':
    case 'string':
    case 'date':
    case 'oid':
      return type;
    default:
      // Treat unknown scalar-ish types as string so they remain pickable.
      if (type.includes('string') || type.includes('char')) return 'string';
      return null;
  }
}

/**
 * Operators the UI may offer for a field, limited to what
 * `evaluateConditions.ts` currently honors for custom layers: numeric compare
 * for numbers; equality only for strings. Dates return empty so the UI can
 * disable with a "coming soon" note.
 */
export function operatorsForField(
  field: ArcGisFieldSchema | null | undefined
): ArcGisConditionOperator[] {
  if (!field) return [];
  if (field.type === 'date') return [];
  if (isNumericFieldType(field.type)) {
    return ['>=', '>', '<=', '<', '=='];
  }
  return ['=='];
}

/** Prefer semantics overrides (e.g. STORMTYPE ordinal) over type inference. */
export function operatorsForFieldWithSemantics(
  field: ArcGisFieldSchema | null | undefined,
  semantics?: { operators?: ArcGisConditionOperator[] } | null
): ArcGisConditionOperator[] {
  if (semantics?.operators?.length) return [...semantics.operators];
  return operatorsForField(field);
}

export function operatorLabel(op: ArcGisConditionOperator): string {
  switch (op) {
    case '>=':
      return '≥';
    case '>':
      return '>';
    case '<=':
      return '≤';
    case '<':
      return '<';
    case '==':
      return '=';
  }
}

function escapeSqlLiteral(value: string): string {
  return value.replace(/'/g, "''");
}

export type ArcGisWhereSemantics = {
  orderedValues?: ArcGisCodedValue[];
};

/**
 * Build an ArcGIS SQL92 WHERE clause for preview queries only. The scheduled
 * evaluator still fetches `where=1=1` this pass.
 *
 * When `semantics.orderedValues` is set (e.g. STORMTYPE intensity ladder),
 * non-numeric inequalities expand to an IN-list of matching codes, including
 * TY/TC aliases whenever HU is included.
 */
export function buildArcGisWhere(
  field: string,
  operator: ArcGisConditionOperator,
  value: string,
  semantics?: ArcGisWhereSemantics | null
): string {
  const trimmed = value.trim();
  if (!field || !trimmed) return '1=0';

  if (semantics?.orderedValues?.length) {
    const codes = ordinalCodesMatching(
      semantics.orderedValues,
      operator,
      trimmed
    );
    if (codes.length === 0) return '1=0';
    if (operator === '==' && codes.length === 1) {
      return `${field} = '${escapeSqlLiteral(codes[0])}'`;
    }
    const list = codes.map((c) => `'${escapeSqlLiteral(c)}'`).join(',');
    return `${field} IN (${list})`;
  }

  const asNum = Number(trimmed);
  const isNumeric = Number.isFinite(asNum) && trimmed !== '';

  if (operator === '==') {
    if (isNumeric) return `${field} = ${asNum}`;
    return `${field} = '${escapeSqlLiteral(trimmed)}'`;
  }

  if (!isNumeric) {
    // Non-numeric inequality is unsupported by the evaluator; refuse preview.
    return '1=0';
  }

  return `${field} ${operator} ${asNum}`;
}

export function automationReadinessLabel(
  authType: ExternalLayerAccessType | undefined
): { label: string; runsInBackground: boolean } {
  if (authType === 'userAuth') {
    return {
      label: 'Requires your ArcGIS sign-in',
      runsInBackground: false,
    };
  }
  return {
    label: 'Runs in background',
    runsInBackground: true,
  };
}

export function snapshotField(field: ArcGisFieldSchema): ArcGisFieldSnapshot {
  return {
    name: field.name,
    alias: field.alias,
    type: field.type,
    codedValues: field.codedValues
      ? field.codedValues.map((c) => ({ ...c }))
      : undefined,
  };
}

/** True when URL is a FeatureServer/MapServer root with no layer index. */
export function isArcGisServiceRoot(url: string): boolean {
  const cleaned = url.trim().replace(/\/+$/, '');
  return /\/(FeatureServer|MapServer)$/i.test(cleaned);
}

/** Append `/n` when missing, or replace trailing index. */
export function withSublayerIndex(url: string, index: number): string {
  const cleaned = url.trim().replace(/\/+$/, '');
  if (/\/(FeatureServer|MapServer)\/\d+$/i.test(cleaned)) {
    return cleaned.replace(/\/\d+$/, `/${index}`);
  }
  if (/\/(FeatureServer|MapServer)$/i.test(cleaned)) {
    return `${cleaned}/${index}`;
  }
  return cleaned;
}
